import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles
} from '@material-ui/core';

import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import { partialUpdateCustomerInfo, updateCustomerInfo } from '../../../services/customer.service';
import { getDoctorList, getHospitalList} from '../../../services/user.service';

const useStyles = makeStyles(() => ({
  field: {
    marginBottom: 10,
    width: '100%'
  },
  select: {
    width: '100%'
  },
  formControl: {
    width: '100%'
  }
}));

const EditSelfEnrolledPatient = ({ data }) => {
  const { patient_id, program_id, treatment_centre, physician_name, hcp_id } = data;
  const classes = useStyles();
  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [hospitalList, setHospitalList] = useState([]);
  const [doctorList, setDoctorList] = useState([]);

  useEffect(() => {
    setSelectedHospital(treatment_centre);

    const getDoctors = async () => {
      const { result } = await getDoctorList(program_id, treatment_centre);

      if (result) {
        setDoctorList(result);
      }
    }
    
    getDoctors();
    setSelectedDoctor(hcp_id);
  }, [treatment_centre, hcp_id]);

  useEffect(() => {
		const getHospitals = async () => {
			const { result } = await getHospitalList(program_id, true);

      if (result) {
			  setHospitalList(result);
      }
		}

			getHospitals();
	}, []);

  const onHospitalSelection = async (e) => {
    const hospital = e.target.value;
    if (hospital 
      && selectedHospital !== hospital) {
      setSelectedHospital(hospital);
      const { result } = await getDoctorList(program_id, hospital);

      if (result) {
        setDoctorList(result);
      }
    }
  }

  const [state, setState] = useState({
    openModal: true,
    openConfirmation: false,
    modalTitle: 'Patient Self Enrollment',
    errorMessage: null,
    successMessage: 'Patient Registration has been updated!',
    isLoading: false,
    latestMessage: '',
    messageLogId: null,
    submitButtonEnabled: false,
  });

  const handleClose = () => {
    const { openConfirmation } = state;
    setState(prev => ({
      ...prev,
      openModal: false,
      openConfirmation: false,
      errorMessage: null
    }));

    if (openConfirmation) {
      window.location.reload();
    }
  };

  const onApprove = async () => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const { message } = await updateCustomerInfo({
        customer_id: patient_id,
        program_id,
        payload: {
          registered_by_user: selectedDoctor,
          metadata: {
            hospital_name: selectedHospital,
            treating_physician: doctorList.find((doctor) => doctor.id === selectedDoctor).full_name
          }
        }
      });

      setState(prev => ({
        ...prev,
        openConfirmation: true,
        openModal: false,
        successMessage: message
      }));
    } catch (e) {
      setState(prev => ({
        ...prev,
        errorMessage: e.message
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const {
    openModal,
    openConfirmation,
    modalTitle,
    errorMessage,
    successMessage,
    isLoading,
    submitButtonEnabled
  } = state;

  if (isLoading) return <LoadingOverlay />;

  const formContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
          <InputLabel>Select Hospital/Clinic</InputLabel>
          <Select
            required={true}
            name="action_dropdown"
            variant='outlined' 
            className={classes.field}
            onChange={onHospitalSelection}
            value={selectedHospital}
          >
            {
                hospitalList && hospitalList.length ? hospitalList?.map(hospital => {
                  return <MenuItem key={hospital} value={hospital}>
                      {hospital}
                  </MenuItem>
                }) : <></>
            }
        </Select>
      </Grid>
      <Grid item xs={12}>
          <InputLabel>Select Your Doctor</InputLabel>
          <Select
            required={true}
            name="action_dropdown"
            variant='outlined' 
            className={classes.field}
            onChange={(e: any) => {
              if (e && e.target && e.target.value) {
                setSelectedDoctor(e.target.value);
                setState(prev => ({
                  ...prev,
                  submitButtonEnabled: true
                }));
              } 
            }}
            value={selectedDoctor}
          >
            {
                doctorList && doctorList.length ? doctorList?.map(doctor => {
                  return <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.full_name}
                  </MenuItem>
                }) : <></>
            }
        </Select>
      </Grid>
      {errorMessage && (
        <Grid item xs={12}>
          <Typography variant="caption" color="error">
            {errorMessage}
          </Typography>
        </Grid>
      )}
    </Grid>
  );

  const formButtons = (
    <>
      <Button onClick={handleClose} variant="contained">
        {openConfirmation ? 'OK' : 'Close'}
      </Button>
      {
        !openConfirmation && 
        <Button
          onClick={onApprove}
          variant="contained"
          color="primary"
          disabled={!submitButtonEnabled}
        >
          Update
        </Button>
      }
    </>
  );

  return (
    <>
      <Dialog
        key="approve-self-enrolled-patient"
        open={openModal}
        title={modalTitle}
        content={formContent}
        children={formButtons}
        onSubmit={onApprove}
        onClose={handleClose}
      />
      <Dialog 
        onSubmit={handleClose}
        key="confirmation"
        open={openConfirmation}
        title="Update Registration"
        content={successMessage}
        children={formButtons}
        onClose={handleClose}
      />
    </>
  );
};

export default EditSelfEnrolledPatient;